'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Users, BookOpen, TrendingUp, FileText, Plus, Eye } from 'lucide-react';

export function HRIntegration() {
  const [selectedTab, setSelectedTab] = useState('recruitment');

  const recruitmentData = [
    {
      id: 1,
      position: '시니어 백엔드 개발자',
      department: '개발부',
      jobGrade: 'L4',
      status: '모집중',
      applicants: 42,
      interviewStage: '1차 면접',
      requiredSkills: ['Java', 'Spring Boot', 'MySQL', 'AWS'],
      postedDate: '2024.01.15',
      deadline: '2024.02.15'
    },
    {
      id: 2,
      position: '마케팅 매니저',
      department: '마케팅부',
      jobGrade: 'L3',
      status: '서류심사',
      applicants: 28,
      interviewStage: '서류심사',
      requiredSkills: ['디지털 마케팅', 'Google Analytics', 'SQL', '데이터 분석'],
      postedDate: '2024.01.20',
      deadline: '2024.02.20'
    }
  ];

  const promotionData = [
    {
      id: 1,
      employee: '김개발',
      currentPosition: '프론트엔드 개발자',
      currentGrade: 'L3',
      targetPosition: '시니어 프론트엔드 개발자',
      targetGrade: 'L4',
      status: '심사중',
      competencyScore: 88,
      meetsCriteria: true,
      requirements: ['3년 이상 경력', 'React 고급 과정 이수', '팀 리드 경험']
    },
    {
      id: 2,
      employee: '박영업',
      currentPosition: '영업 대리',
      currentGrade: 'L2',
      targetPosition: '영업 매니저',
      targetGrade: 'L3',
      status: '승인',
      competencyScore: 92,
      meetsCriteria: true,
      requirements: ['영업 실적 120% 달성', '고객 관리 과정 이수']
    }
  ];

  const trainingData = [
    {
      id: 1,
      program: 'React 고급 개발과정',
      targetPosition: '프론트엔드 개발자',
      targetGrade: 'L3-L4',
      duration: '40시간',
      mode: '온라인',
      participants: 12,
      status: '진행중',
      competencyGap: ['고급 React 패턴', 'TypeScript', '성능 최적화']
    },
    {
      id: 2,
      program: '영업 전략 및 협상 기법',
      targetPosition: '영업 매니저',
      targetGrade: 'L3-L4',
      duration: '24시간',
      mode: '집합교육',
      participants: 8,
      status: '예정',
      competencyGap: ['전략적 사고', '협상 기법', '고객 관계 관리']
    },
    {
      id: 3,
      program: 'Data-driven 마케팅',
      targetPosition: '마케팅 스페셜리스트',
      targetGrade: 'L2-L3',
      duration: '32시간',
      mode: '블렌디드',
      participants: 15,
      status: '모집중',
      competencyGap: ['데이터 분석', 'SQL 활용', '마케팅 자동화']
    }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">채용/승진/교육 연계</h1>
          <p className="text-xs sm:text-sm text-gray-600">직무 기반 인재 관리 및 개발</p>
        </div>
        <Button size="sm" className="text-xs sm:text-sm w-fit">
          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">새 </span>프로그램 등록
        </Button>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
        <TabsList>
          <TabsTrigger value="recruitment" className="text-[10px] sm:text-sm px-2 sm:px-4">채용 관리</TabsTrigger>
          <TabsTrigger value="promotion" className="text-[10px] sm:text-sm px-2 sm:px-4">승진 관리</TabsTrigger>
          <TabsTrigger value="training" className="text-[10px] sm:text-sm px-2 sm:px-4">교육 관리</TabsTrigger>
        </TabsList>

        <TabsContent value="recruitment" className="space-y-4 sm:space-y-6">
          {/* Recruitment Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">활성 채용공고</CardTitle>
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">12개</div>
                <p className="text-[10px] sm:text-xs text-gray-500">전월 대비 +3개</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">총 지원자</CardTitle>
                <Users className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">284명</div>
                <p className="text-[10px] sm:text-xs text-gray-500">평균 23.7명/공고</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">면접 진행률</CardTitle>
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">68%</div>
                <p className="text-[10px] sm:text-xs text-gray-500">서류 통과율</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">평균 채용기간</CardTitle>
                <Users className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">28일</div>
                <p className="text-[10px] sm:text-xs text-gray-500">목표 30일 이내</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">채용 현황</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무기술서 기반 자동 생성된 채용공고</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">직무명</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">등급</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">지원현황</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">마감일</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">상태</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recruitmentData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">{item.position}</div>
                          <div className="text-[10px] sm:text-sm text-muted-foreground">{item.department}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] sm:text-xs">{item.jobGrade}</Badge>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="font-medium text-xs sm:text-sm">{item.applicants}명 지원</div>
                          <div className="text-[10px] sm:text-sm text-muted-foreground">{item.interviewStage}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs sm:text-sm">{item.deadline}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={item.status === '모집중' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                            {item.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">직무기술서 기반 JD 자동생성</CardTitle>
              <CardDescription className="text-xs sm:text-sm">등록된 직무분석 데이터를 활용한 채용공고 생성</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium">직무 선택</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="직무를 선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="frontend">프론트엔드 개발자</SelectItem>
                        <SelectItem value="backend">백엔드 개발자</SelectItem>
                        <SelectItem value="marketing">마케팅 스페셜리스트</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium">채용 등급</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="등급을 선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L2">L2 (주니어)</SelectItem>
                        <SelectItem value="L3">L3 (시니어)</SelectItem>
                        <SelectItem value="L4">L4 (리드)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button size="sm" className="text-xs sm:text-sm">JD 자동 생성</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promotion" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">승진 심사 현황</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무등급 기준 승진 요건 자동 반영</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">직원</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">현재 → 목표</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">역량</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">상태</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promotionData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium text-xs sm:text-sm">{item.employee}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <Badge variant="outline" className="text-[10px] sm:text-xs">{item.currentGrade}</Badge>
                            <span className="text-muted-foreground text-xs">→</span>
                            <Badge variant="outline" className="text-[10px] sm:text-xs">{item.targetGrade}</Badge>
                          </div>
                          <div className="text-[10px] sm:text-sm text-muted-foreground mt-1 truncate max-w-[100px] sm:max-w-none">{item.targetPosition}</div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <span className="text-xs sm:text-sm">{item.competencyScore}점</span>
                            <Progress value={item.competencyScore} className="w-10 sm:w-12" />
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant={item.meetsCriteria ? 'default' : 'destructive'} className="text-[10px] sm:text-xs">
                              {item.meetsCriteria ? '충족' : '미충족'}
                            </Badge>
                            <Badge variant={item.status === '승인' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                              {item.status}
                            </Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">승진 요건 관리</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무등급별 승진 기준 설정</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium">현재 등급</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="현재 등급" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L1">L1</SelectItem>
                        <SelectItem value="L2">L2</SelectItem>
                        <SelectItem value="L3">L3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium">목표 등급</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="목표 등급" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L2">L2</SelectItem>
                        <SelectItem value="L3">L3</SelectItem>
                        <SelectItem value="L4">L4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs sm:text-sm font-medium">최소 재직기간</label>
                    <Input placeholder="개월 수" type="number" className="text-sm mt-1" />
                  </div>
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium">승진 요건</label>
                  <Textarea placeholder="승진을 위한 구체적인 요건을 입력하세요..." className="text-sm mt-1" />
                </div>
                <Button size="sm" className="text-xs sm:text-sm">요건 저장</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="training" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">교육 프로그램 현황</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무별 부족 역량 기반 교육 과정 추천</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">프로그램</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">대상</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">교육정보</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">상태</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trainingData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{item.program}</div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="text-xs sm:text-sm">{item.targetPosition}</div>
                          <Badge variant="outline" className="mt-1 text-[10px] sm:text-xs">{item.targetGrade}</Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="text-xs sm:text-sm">{item.duration} • {item.mode}</div>
                          <div className="text-[10px] sm:text-sm text-muted-foreground">{item.participants}명 참여</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={
                            item.status === '진행중' ? 'default' :
                            item.status === '예정' ? 'secondary' : 'outline'
                          } className="text-[10px] sm:text-xs">
                            {item.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">역량 갭 분석 및 교육 추천</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무별 요구 역량과 현재 역량 갭 분석</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium">직무 선택</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="분석할 직무를 선택하세요" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="frontend">프론트엔드 개발자</SelectItem>
                        <SelectItem value="backend">백엔드 개발자</SelectItem>
                        <SelectItem value="marketing">마케팅 스페셜리스트</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium">분석 대상</label>
                    <Select>
                      <SelectTrigger className="text-sm mt-1">
                        <SelectValue placeholder="분석 범위 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">개인별</SelectItem>
                        <SelectItem value="team">팀별</SelectItem>
                        <SelectItem value="department">부서별</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button size="sm" className="text-xs sm:text-sm">
                  <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  역량 갭 분석 시작
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
